/**
 * Mutation functions for Fehlzeiten (Absence) service layer.
 *
 * All write operations for absences go through this module.
 * NO direct Supabase imports should exist in UI components.
 *
 * @module fehlzeiten/services/mutations
 */

import { supabase } from "../../../../lib/supabase";
import type {
  CreateAbsenceParams,
  UpdateAbsenceParams,
  DeleteAbsenceParams,
  ServiceResult,
  AbsenceConflict,
} from "../types/services";
import { toIsoDate, normalizeTime } from "./helpers";

/** Prefix used by the RPC to signal duplicate absence conflicts */
const CONFLICT_ERROR_PREFIX = "DUPLICATE_ABSENCE_CONFLICT::";

// Re-export internal mutations for backward compatibility
export {
  updateAbsenceNote,
  softDeleteAbsenceNote,
  createAbsenceNote,
  clearNoteApproval,
  updateLatenessEntries,
  createNoteFromDailyLog,
  linkDailyLogToNote,
  unlinkDailyLogFromNote,
  updateDailyLogNotes,
  mergeAbsenceNotes,
  flipAttendanceToExcused,
  type MergeAbsenceNotesParams,
} from "./internalMutations";

// ============================================================================
// Create Operations
// ============================================================================

/**
 * Creates a recurring absence using the backend recurrence function.
 *
 * This is more efficient than calling createAbsence multiple times.
 * The backend generates all dates based on the recurrence pattern.
 *
 * @param params - Recurring absence creation parameters
 * @returns Service result indicating success or failure
 */
export async function createAbsenceWithRecurrence(params: {
  studentId: string;
  schoolId: string;
  date: string;
  status: "krankgemeldet" | "beurlaubt" | "unentschuldigt" | "verspätet";
  reason?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  createdBy?: string | null;
  recurrence: {
    startDate: string;
    endDate: string;
    repeatEveryUnit: "day";
    weekDays?: number[];
  };
  forceOverwrite?: boolean;
}): Promise<ServiceResult> {
  const {
    studentId,
    schoolId,
    date,
    status,
    reason,
    startTime,
    endTime,
    createdBy,
    recurrence,
  } = params;

  if (!studentId || !schoolId) {
    return { success: false, error: "Student ID and School ID are required" };
  }

  // Get current user ID from Supabase auth if not provided
  let createdById = createdBy;
  if (!createdById) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    createdById = user?.id || null;
  }

  if (!createdById) {
    return {
      success: false,
      error: "User not authenticated - cannot determine created_by",
    };
  }

  const dateIso = toIsoDate(date);
  const normalizedStartTime = normalizeTime(startTime);
  const normalizedEndTime = normalizeTime(endTime);

  // Map status to absence_status format expected by backend
  const absenceStatus =
    status === "krankgemeldet"
      ? "krankgemeldet"
      : status === "beurlaubt"
      ? "beurlaubt"
      : status === "unentschuldigt"
      ? "unentschuldigt"
      : "unentschuldigt";

  // Build recurrence JSONB
  const recurrenceJson = {
    startDate: toIsoDate(recurrence.startDate),
    endDate: toIsoDate(recurrence.endDate),
    repeatEveryUnit: recurrence.repeatEveryUnit,
    weekDays: recurrence.weekDays || [],
  };

  // Convert normalized times to time format (HH:MM -> HH:MM:SS), but don't double-add seconds
  const toTimeStr = (t: string | null, fallback: string) => {
    if (!t) return fallback;
    // Already has seconds (HH:MM:SS)?
    return t.split(":").length >= 3 ? t : `${t}:00`;
  };
  const fromTime = toTimeStr(normalizedStartTime, "00:00:00");
  const toTime = toTimeStr(normalizedEndTime, "23:59:59");

  const { data, error } = await supabase.rpc(
    "add_student_absence_with_times_and_recurrence" as never,
    {
      p_student_id: studentId,
      p_school_id: schoolId,
      p_absence_date: dateIso,
      p_from_time: fromTime,
      p_to_time: toTime,
      p_reason: reason || null,
      p_excused: status === "krankgemeldet" || status === "beurlaubt",
      p_attachment_url: null,
      p_created_by: createdBy || null,
      p_recurrence: recurrenceJson,
      p_absence_status: absenceStatus,
      p_force_overwrite: params.forceOverwrite ?? false,
    } as never,
  );

  if (error) {
    return { success: false, error: error.message };
  }

  // Check for conflict signal in response data (returned via notes column)
  const rows = data as any[];
  if (rows?.[0]?.notes?.startsWith(CONFLICT_ERROR_PREFIX)) {
    try {
      const jsonStr = rows[0].notes.split(CONFLICT_ERROR_PREFIX)[1];
      const conflicts: AbsenceConflict[] = JSON.parse(jsonStr);
      return {
        success: false,
        error: "DUPLICATE_ABSENCE_CONFLICT",
        conflicts,
      };
    } catch {
      return { success: false, error: "Failed to parse conflict data" };
    }
  }

  return { success: true };
}

/**
 * Creates a new absence record for a student.
 *
 * Handles all absence types: krankgemeldet, beurlaubt, unentschuldigt, verspätet.
 * For multi-day absences, creates the appropriate date range.
 *
 * @param params - Absence creation parameters
 * @returns Service result indicating success or failure
 */
export async function createAbsence(
  params: CreateAbsenceParams & { forceOverwrite?: boolean },
): Promise<ServiceResult> {
  const {
    studentId,
    date,
    status,
    reason,
    endDate,
    checkInTime,
    startTime,
    endTime,
    forceOverwrite,
  } = params;

  if (!studentId) {
    return { success: false, error: "Student ID is required" };
  }

  const dateIso = toIsoDate(date);
  const endDateIso = endDate ? toIsoDate(endDate) : null;
  const normalizedStartTime = normalizeTime(startTime);
  const normalizedEndTime = normalizeTime(endTime);
  const normalizedCheckInTime = normalizeTime(checkInTime);

  const { data, error } = await supabase.rpc(
    "create_student_absence_react" as never,
    {
      p_student_id: studentId,
      p_date: dateIso,
      p_status: status,
      p_reason: reason || null,
      p_end_date: endDateIso,
      p_check_in_time: normalizedCheckInTime,
      p_start_time: normalizedStartTime,
      p_end_time: normalizedEndTime,
      p_force_overwrite: forceOverwrite ?? false,
    } as never,
  );

  if (error) {
    return { success: false, error: error.message };
  }

  // Check for conflict signal in response data
  const result = data as { conflict?: boolean; conflicts?: any[] } | null;
  if (result?.conflict === true && result.conflicts) {
    return {
      success: false,
      error: "DUPLICATE_ABSENCE_CONFLICT",
      conflicts: result.conflicts as AbsenceConflict[],
    };
  }

  return { success: true };
}

/**
 * Creates multiple absence records in batch.
 *
 * @param entries - Array of absence entries to create
 * @returns Service result with count of created entries
 */
export async function createAbsencesBatch(
  entries: CreateAbsenceParams[],
): Promise<ServiceResult<{ created: number }>> {
  let created = 0;
  const errors: string[] = [];

  for (const entry of entries) {
    const result = await createAbsence(entry);
    if (result.success) {
      created++;
    } else {
      errors.push(result.error || "Unknown error");
    }
  }

  if (errors.length > 0 && created === 0) {
    return { success: false, error: errors.join("; ") };
  }

  return { success: true, data: { created } };
}

// ============================================================================
// Update Operations
// ============================================================================

/**
 * Updates an existing absence record.
 *
 * If the original record is not found (404), falls back to delete and recreate.
 *
 * @param params - Update parameters including the original absence note ID
 * @returns Service result indicating success or failure
 */
export async function updateAbsence(
  params: UpdateAbsenceParams,
): Promise<ServiceResult> {
  const {
    studentId,
    date,
    status,
    reason,
    endDate,
    checkInTime,
    absenceNoteId,
    startTime,
    endTime,
  } = params;

  const dateIso = toIsoDate(date);
  const endDateIso = endDate ? toIsoDate(endDate) : null;
  const normalizedStartTime = normalizeTime(startTime);
  const normalizedEndTime = normalizeTime(endTime);
  const normalizedCheckInTime = normalizeTime(checkInTime);

  const { error } = await supabase.rpc(
    "update_student_absence_react" as never,
    {
      p_student_id: studentId,
      p_date: dateIso,
      p_status: status,
      p_reason: reason || null,
      p_end_date: endDateIso,
      p_check_in_time: normalizedCheckInTime,
      p_absence_note_id: absenceNoteId || null,
      p_start_time: normalizedStartTime,
      p_end_time: normalizedEndTime,
    } as never,
  );

  if (error) {
    // Check for 404/not found - fallback to delete and recreate
    if (/404|not\s*found|does not exist/i.test(error.message)) {
      await deleteAbsence({ studentId, date: dateIso, status });
      return createAbsence({
        studentId,
        date: dateIso,
        status: status as CreateAbsenceParams["status"],
        reason,
        endDate: endDateIso || undefined,
        checkInTime: normalizedCheckInTime || undefined,
        startTime: normalizedStartTime || undefined,
        endTime: normalizedEndTime || undefined,
      });
    }
    return { success: false, error: error.message };
  }

  return { success: true };
}

// ============================================================================
// Delete Operations
// ============================================================================

/**
 * Deletes an absence record for a student on a specific date.
 *
 * @param params - Delete parameters
 * @returns Service result indicating success or failure
 */
export async function deleteAbsence(
  params: DeleteAbsenceParams,
): Promise<ServiceResult> {
  const { studentId, date, status } = params;
  const dateIso = toIsoDate(date);

  const { error } = await supabase.rpc(
    "delete_absences_for_date" as never,
    {
      p_student_id: studentId,
      p_date: dateIso,
      p_status: status,
    } as never,
  );

  if (error) {
    return { success: false, error: error.message };
  }

  return { success: true };
}
